import { trpc } from 'api'
import useSession from 'hooks/useSession'
import { useMemo } from 'react'
import { useOrderState } from './useOrderState'
import { useLastOrderDefaults } from './useSaveOrderDefaults'
import { trpcClientUtils } from 'api/ApiProvider'

export const getPrescriptionByIdFromCache = (
  prescriptionId: number | null | undefined,
  customerId: number | null | undefined
) => {
  if (!prescriptionId || !customerId) return undefined
  return trpcClientUtils.customer.prescriptions.byId.getData({
    prescriptionId: prescriptionId,
    customerId,
  })
}

export const usePrescriptionById = (prescriptionId?: number | null) => {
  const customerId = useSession((s) => s.customer?.data.id)
  const prescription = trpc.customer.prescriptions.byId.useQuery(
    {
      prescriptionId: prescriptionId || 0,
      customerId: customerId || 0,
    },
    {
      enabled: !!prescriptionId && !!customerId,
      staleTime: 1000 * 60 * 15,
    }
  )

  return prescription
}

export const usePrescription = () => {
  const prescriptionId = useOrderState((s) => s.order.prescriptionId)

  return usePrescriptionById(prescriptionId)?.data
}

export const useGetLatestPrescriptions = (options?: {
  enabled?: boolean
}) => {
  const { enabled = true } = options ?? {}
  const customerId = useSession((s) => s.customer?.data.id)
  const query = trpc.customer.prescriptions.list.useQuery(
    {
      limit: 1,
      id: customerId || 0,
    },
    {
      enabled: !!customerId && enabled,
      refetchOnMount: 'always',
    }
  )

  const data = query.data?.data
  const hasPrescriptions = !!(data && data.length > 0)

  return {
    isLoading: query.isLoading,
    prescription: data?.[0] ?? null,
    prescriptions: data,
    hasPrescriptions,
    refetch: query.refetch,
  }
}

export const usePrescriptionWithDefaults = () => {
  const { lastUsedPrescriptionId } = useLastOrderDefaults()
  const hasLastUsed = !!lastUsedPrescriptionId

  const savedQuery = usePrescriptionById(
    hasLastUsed ? lastUsedPrescriptionId : null
  )
  const savedNotFound =
    hasLastUsed && !savedQuery.isLoading && !savedQuery.data

  // Only fetch latest list when no saved ID, or saved prescription not found
  const latest = useGetLatestPrescriptions({
    enabled: !hasLastUsed || savedNotFound,
  })

  // Stable reference — avoids new array on every render
  const savedPrescriptions = useMemo(
    () => (savedQuery.data ? [savedQuery.data] : undefined),
    [savedQuery.data]
  )

  if (hasLastUsed && savedQuery.data) {
    return {
      prescriptions: savedPrescriptions,
      prescription: savedQuery.data,
      hasPrescriptions: true,
      isLoading: false,
      refetch: latest.refetch,
    }
  }

  if (hasLastUsed && savedQuery.isLoading) {
    return {
      prescriptions: undefined,
      prescription: null,
      hasPrescriptions: false,
      isLoading: true,
      refetch: latest.refetch,
    }
  }

  return latest
}
